import React, { useState } from 'react';
import { Box, Typography, Link, Modal, Divider } from '@mui/material';
import { FaFacebook, FaInstagram, FaTiktok, FaWhatsapp, FaEnvelope, FaPhone, FaMapMarkerAlt } from 'react-icons/fa';
import { motion } from 'framer-motion';
import LegalNotice from './legalNotice';
import TermsOfSale from './termsOfSales';

const Footer = () => {
  const [openLegal, setOpenLegal] = useState(false);
  const [openTerms, setOpenTerms] = useState(false);

  const socialLinks = [
    { icon: <FaFacebook size={20} />, href: 'https://www.facebook.com/share/16Bzdpqc7N/?mibextid=wwXIfr' },
    { icon: <FaInstagram size={20} />, href: 'https://www.instagram.com/traiteur_du_soleil_' },
    { icon: <FaTiktok size={20} />, href: 'https://www.tiktok.com/@traiteur_du_soleil?_t=ZN-8vRwmdPOzoM&_r=1' },
    { icon: <FaWhatsapp size={20} />, href: 'https://wa.me/+33785853636' },
  ];

  const handleOpenLegal = () => setOpenLegal(true);
  const handleCloseLegal = () => setOpenLegal(false);
  const handleOpenTerms = () => setOpenTerms(true);
  const handleCloseTerms = () => setOpenTerms(false);

  return (
    <Box 
      component="footer" 
      role="contentinfo" 
      sx={{ 
        backgroundColor: '#FEF3DE', 
        padding: { xs: '40px 24px', md: '48px 40px' }, 
        color: '#5D4037',
        borderTop: '1px solid rgba(139, 69, 19, 0.2)',
        backgroundImage: 'linear-gradient(135deg, #FEF3DE 0%, #FCE4C0 100%)',
      }}
    >
      <Box
        sx={{
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'center', md: 'flex-start' },
          gap: { xs: '32px', md: '0' }
        }}
      >
        {/* Logo and description */}
        <Box sx={{ textAlign: { xs: 'center', md: 'left' }, maxWidth: '320px' }}>
          <Typography variant="h6" sx={{ 
            fontWeight: 'bold', 
            mb: 2, 
            color: '#8B4513',
            fontFamily: "'Playfair Display', serif",
            fontSize: '1.4rem'
          }}>
            Pain du Soleil
          </Typography>
          <Typography variant="body2" sx={{ mb: 3, color: '#5D4037', lineHeight: 1.6 }}>
            Des saveurs authentiques issues des traditions libanaises, slaves et méditerranéennes, pour une expérience culinaire unique et dépaysante.
          </Typography>
        </Box>

        {/* Contact info */}
        <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
          <Typography variant="subtitle2" sx={{ 
            fontWeight: 'bold', 
            mb: 2,
            color: '#8B4513',
            fontSize: '1rem',
            fontFamily: "'Playfair Display', serif",
            position: 'relative',
            paddingBottom: '8px',
            '&:after': {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: { xs: '50%', md: 0 },
              transform: { xs: 'translateX(-50%)', md: 'none' },
              width: '40px',
              height: '2px',
              backgroundColor: 'rgba(139, 69, 19, 0.3)',
            }
          }}>
            Contact
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FaEnvelope size={16} color="#8B4513" />
              <Typography variant="body2">
                <Link href="mailto:contact@paindusoleil.com" sx={{ 
                  color: '#5D4037', 
                  textDecoration: 'none',
                  transition: 'color 0.2s ease',
                  '&:hover': { color: '#8B4513', textDecoration: 'underline' } 
                }}>
                  contact@paindusoleil.com
                </Link>
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FaPhone size={16} color="#8B4513" />
              <Typography variant="body2">
                <Link href="tel:+33785853636" sx={{ 
                  color: '#5D4037', 
                  textDecoration: 'none',
                  transition: 'color 0.2s ease',
                  '&:hover': { color: '#8B4513', textDecoration: 'underline' } 
                }}>
                  +33 7 85 85 36 36
                </Link>
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FaMapMarkerAlt size={16} color="#8B4513" />
              <Typography variant="body2" sx={{ color: '#5D4037' }}>
                Cormeilles-en-Parisis, 95240
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Legal links */}
        <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
          <Typography variant="subtitle2" sx={{ 
            fontWeight: 'bold', 
            mb: 2,
            color: '#8B4513',
            fontSize: '1rem',
            fontFamily: "'Playfair Display', serif",
            position: 'relative',
            paddingBottom: '8px',
            '&:after': {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: { xs: '50%', md: 0 },
              transform: { xs: 'translateX(-50%)', md: 'none' },
              width: '40px',
              height: '2px',
              backgroundColor: 'rgba(139, 69, 19, 0.3)',
            }
          }}>
            Informations légales
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <Link
              component="button"
              onClick={handleOpenLegal}
              sx={{ 
                color: '#5D4037', 
                textDecoration: 'none', 
                fontSize: '0.875rem',
                textAlign: 'left',
                transition: 'color 0.2s ease',
                '&:hover': { color: '#8B4513', textDecoration: 'underline' } 
              }}
              aria-label="Ouvrir les mentions légales"
            >
              Mentions Légales
            </Link>
            <Link
              component="button"
              onClick={handleOpenTerms}
              sx={{ 
                color: '#5D4037', 
                textDecoration: 'none',
                fontSize: '0.875rem',
                textAlign: 'left',
                transition: 'color 0.2s ease',
                '&:hover': { color: '#8B4513', textDecoration: 'underline' } 
              }}
              aria-label="Ouvrir les conditions générales de vente"
            >
              Conditions Générales de Vente
            </Link>
          </Box>
        </Box>

        {/* Social links */}
        <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
          <Typography variant="subtitle2" sx={{ 
            fontWeight: 'bold', 
            mb: 2,
            color: '#8B4513',
            fontSize: '1rem',
            fontFamily: "'Playfair Display', serif",
            position: 'relative',
            paddingBottom: '8px',
            '&:after': {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: { xs: '50%', md: 0 },
              transform: { xs: 'translateX(-50%)', md: 'none' },
              width: '40px',
              height: '2px',
              backgroundColor: 'rgba(139, 69, 19, 0.3)',
            }
          }}>
            Réseaux sociaux
          </Typography>
          <Box sx={{ display: 'flex', gap: '16px', justifyContent: { xs: 'center', md: 'flex-start' } }}>
            {socialLinks.map((link, index) => (
              <motion.div
                key={index}
                whileHover={{ scale: 1.1, rotate: 10 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link 
                  href={link.href} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  sx={{ 
                    color: '#8B4513',
                    transition: 'color 0.2s ease',
                    '&:hover': { color: '#A0522D' }
                  }}
                >
                  {link.icon}
                </Link>
              </motion.div>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Divider */}
      <Divider sx={{ my: 4, borderColor: 'rgba(139, 69, 19, 0.2)' }} />

      {/* Copyright */}
      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="body2" sx={{ color: '#5D4037', fontSize: '0.75rem' }}>
          © 2025 Pain du Soleil. Tous droits réservés. <Box component="span" sx={{ opacity: 0, visibility: 'hidden', fontSize: 0 }}>Made by <Link href="https://brxdigital.com" target="_blank" rel="noopener noreferrer" sx={{ color: '#8B4513', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>brxdigital.com</Link></Box>
        </Typography>
      </Box>

      {/* Modals */}
      <Modal
        open={openLegal}
        onClose={handleCloseLegal}
        aria-labelledby="modal-legal-title"
        aria-describedby="modal-legal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: '#FEF9EF',
            boxShadow: '0 10px 25px rgba(139, 69, 19, 0.2)',
            p: 4,
            maxHeight: '80vh',
            overflowY: 'auto',
            borderRadius: '12px',
            width: { xs: '90%', sm: '80%', md: '60%' },
            maxWidth: '800px',
            outline: 'none',
            border: '1px solid rgba(139, 69, 19, 0.1)',
          }}
        >
          <LegalNotice />
        </Box>
      </Modal>

      <Modal
        open={openTerms}
        onClose={handleCloseTerms}
        aria-labelledby="modal-terms-title"
        aria-describedby="modal-terms-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: '#FEF9EF',
            boxShadow: '0 10px 25px rgba(139, 69, 19, 0.2)',
            p: 4,
            maxHeight: '80vh',
            overflowY: 'auto',
            borderRadius: '12px',
            width: { xs: '90%', sm: '80%', md: '60%' },
            maxWidth: '800px',
            outline: 'none',
            border: '1px solid rgba(139, 69, 19, 0.1)',
          }}
        >
          <TermsOfSale />
        </Box>
      </Modal>
    </Box>
  );
};

export default Footer;